import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { samplesApi, type Sample } from '@/lib/api';
import { DASHBOARD_PAGE_SIZE } from '@/components/PaginatedListFooter';
import { toast } from 'sonner';

function parsePaginated<T>(data: { results?: T[]; count?: number } | T[]): { items: T[]; total: number } {
  if (Array.isArray(data)) return { items: data, total: data.length };
  return { items: data.results ?? [], total: data.count ?? 0 };
}

export function useSamples(search?: string, page = 1) {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [total, setTotal] = useState(0);
  const [inReviewCount, setInReviewCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const listParams: Record<string, string> = {
        page: String(page),
        page_size: String(DASHBOARD_PAGE_SIZE),
      };
      if (search) listParams.search = search;

      const reviewParams: Record<string, string> = {
        status: 'in_review',
        page_size: '1',
      };
      if (search) reviewParams.search = search;

      const completedParams: Record<string, string> = {
        page_size: '1',
      };

      const [{ data: listData }, { data: reviewData }, { data: completedData }] = await Promise.all([
        samplesApi.list(listParams),
        samplesApi.list(reviewParams),
        samplesApi.completed(completedParams),
      ]);

      const parsed = parsePaginated(listData);
      setSamples(parsed.items);
      setTotal(parsed.total);
      setInReviewCount(parsePaginated(reviewData).total);
      setCompletedCount(parsePaginated(completedData).total);
    } catch (err) {
      // DRF returns 404 when the requested page no longer exists (e.g. deleted the last
      // page). Keep counts and let the dashboard step back a page instead of showing
      // an empty catalog.
      const emptyPage = page > 1 && axios.isAxiosError(err) && err.response?.status === 404;
      if (emptyPage) {
        setSamples([]);
        return;
      }
      toast.error('Failed to load samples');
      setSamples([]);
      setTotal(0);
      setInReviewCount(0);
      setCompletedCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    samples,
    total,
    inReviewCount,
    completedCount,
    loading,
    refresh,
    pageSize: DASHBOARD_PAGE_SIZE,
  };
}
